<template>
  <div class="min-h-screen bg-gray-50">
    <!-- 🎨 Header Moderno -->
    <header
      class="sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b border-gray-200 shadow-sm"
    >
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between h-16">
          <!-- Logo & Title -->
          <div class="flex items-center space-x-4 group">
            <div class="relative">
              <div
                class="absolute inset-0 bg-blue-600 rounded-xl blur-lg opacity-20 group-hover:opacity-30 transition-opacity duration-300"
              ></div>
              <div
                class="relative bg-blue-600 p-3 rounded-xl shadow-md group-hover:scale-105 transition-transform duration-300"
              >
                <svg
                  class="w-6 h-6 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
            </div>
            <div>
              <h1 class="text-2xl font-bold text-gray-900">Dashboard</h1>
              <p class="text-sm text-gray-500 font-medium">Gestão do Sistema</p>
            </div>
          </div>

          <!-- Actions -->
          <div class="flex items-center space-x-3">
            <!-- Refresh Button -->
            <button
              @click="fetchStats"
              :disabled="loading"
              class="group flex items-center space-x-2 px-4 py-2 bg-white hover:bg-gray-50 border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg
                class="w-4 h-4 text-gray-600 transition-transform duration-500"
                :class="{ 'animate-spin': loading }"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              <span class="text-sm font-medium text-gray-700 hidden sm:inline">
                Atualizar
              </span>
            </button>

            <!-- User Menu -->
            <div
              class="flex items-center space-x-3 px-3 py-1.5 bg-blue-50 rounded-xl border border-blue-200"
            >
              <div
                class="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center"
              >
                <svg
                  class="w-4 h-4 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              </div>
              <span class="text-sm font-semibold text-gray-700 hidden md:inline"
                >Admin</span
              >
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- 📊 Navigation Tabs -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
      <div
        class="bg-white/90 backdrop-blur-xl rounded-2xl shadow-sm border border-gray-200 p-2"
      >
        <nav class="flex space-x-2 overflow-x-auto scrollbar-hide">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            @click="activeTab = tab.id"
            class="group relative flex items-center space-x-3 px-5 py-3 rounded-xl font-medium text-sm transition-all duration-300 whitespace-nowrap"
            :class="[
              activeTab === tab.id
                ? 'bg-blue-600 text-white shadow-lg'
                : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
            ]"
          >
            <!-- Icon -->
            <div class="relative z-10">
              <svg
                class="w-5 h-5 transition-transform duration-300 group-hover:scale-110"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  v-if="tab.id === 'overview'"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                />
                <path
                  v-else-if="tab.id === 'users'"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
                <path
                  v-else-if="tab.id === 'posts'"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
                <path
                  v-else-if="tab.id === 'analytics'"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
                <path
                  v-else-if="tab.id === 'moderation'"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
            </div>

            <!-- Label -->
            <span class="relative z-10 font-semibold">{{ tab.label }}</span>

            <!-- Badge -->
            <span
              v-if="tab.badge"
              class="relative z-10 px-2 py-0.5 text-xs font-bold rounded-full"
              :class="[
                activeTab === tab.id
                  ? 'bg-white/20 text-white'
                  : 'bg-blue-100 text-blue-700',
              ]"
            >
              {{ tab.badge }}
            </span>
          </button>
        </nav>
      </div>
    </div>
    <!-- 📱 Main Content com Animações -->
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <transition
        name="fade-slide"
        mode="out-in"
        @before-enter="beforeEnter"
        @enter="enter"
      >
        <!-- Overview Tab -->
        <div v-if="activeTab === 'overview'" key="overview" class="animate-in">
          <OverviewTab
            :stats="stats"
            :loading="loading"
            @refresh="fetchStats"
            @retry="fetchStats"
          />
        </div>

        <!-- Users Management -->
        <div v-else-if="activeTab === 'users'" key="users" class="animate-in">
          <UsersTab />
        </div>

        <!-- Posts Management -->
        <div v-else-if="activeTab === 'posts'" key="posts" class="animate-in">
          <PostsTab />
        </div>

        <!-- Analytics -->
        <div
          v-else-if="activeTab === 'analytics'"
          key="analytics"
          class="animate-in"
        >
          <AnalyticsTab />
        </div>

        <!-- Moderation Logs -->
        <div
          v-else-if="activeTab === 'moderation'"
          key="moderation"
          class="animate-in"
        >
          <ModerationTab />
        </div>
      </transition>
    </main>

    <!-- 🎯 Footer -->
    <footer
      class="mt-auto py-6 border-t border-gray-200/50 bg-white/50 backdrop-blur-sm"
    >
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          class="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0"
        >
          <div class="flex items-center space-x-2 text-sm text-gray-600">
            <svg
              class="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span>Sistema operando normalmente</span>
          </div>

          <div class="text-sm text-gray-500">
            © {{ new Date().getFullYear() }} Blog Dashboard. Desenvolvido com
            <span class="text-red-500 animate-pulse">♥</span>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { ref, onMounted } from "vue";
import apiService from "@/services/api";
import OverviewTab from "@/components/admin/OverviewTab.vue";
import UsersTab from "@/components/admin/UsersTab.vue";
import PostsTab from "@/components/admin/PostsTab.vue";
import AnalyticsTab from "@/components/admin/AnalyticsTab.vue";
import ModerationTab from "@/components/admin/ModerationTab.vue";

export default {
  name: "AdminDashboard",
  components: {
    OverviewTab,
    UsersTab,
    PostsTab,
    AnalyticsTab,
    ModerationTab,
  },
  setup() {
    const activeTab = ref("overview");
    const stats = ref(null);
    const loading = ref(true);

    const tabs = [
      { id: "overview", label: "Visão Geral", badge: null },
      { id: "users", label: "Usuários", badge: null },
      { id: "posts", label: "Posts", badge: null },
      { id: "analytics", label: "Analytics", badge: null },
      { id: "moderation", label: "Moderação", badge: null },
    ];

    const fetchStats = async () => {
      try {
        loading.value = true;
        const data = await apiService.get(
          "/api/handlers?module=admin&action=stats"
        );
        stats.value = data.data;
      } catch (error) {
        console.error("Erro ao carregar estatísticas:", error);
      } finally {
        loading.value = false;
      }
    };

    // 🎨 Animações de entrada
    const beforeEnter = (el) => {
      el.style.opacity = 0;
      el.style.transform = "translateY(20px)";
    };

    const enter = (el, done) => {
      el.offsetHeight; // trigger reflow
      el.style.transition = "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)";
      el.style.opacity = 1;
      el.style.transform = "translateY(0)";
      done();
    };

    onMounted(() => {
      fetchStats();
    });

    return {
      activeTab,
      tabs,
      stats,
      loading,
      fetchStats,
      beforeEnter,
      enter,
    };
  },
};
</script>

<style scoped>
/* 🎨 Animações Suaves */
.animate-in {
  animation: fadeInUp 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Transição entre tabs */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateX(20px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

/* Scrollbar customizado */
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

/* Hover effects suaves */
.group:hover .group-hover\:scale-110 {
  transform: scale(1.1);
}

.group:hover .group-hover\:opacity-50 {
  opacity: 0.5;
}

.group:hover .group-hover\:opacity-40 {
  opacity: 0.4;
}

/* Animação de spin suave */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}

/* Pulse animation */
.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* Glassmorphism effect */
.backdrop-blur-xl {
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
}

.backdrop-blur-sm {
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

/* Smooth transitions */
* {
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}
</style>
<style scoped>
/* Modern Dashboard Styles */
.admin-dashboard {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 2rem;
}

/* Header Styles */
.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding: 2rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.logo-section {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.logo-icon {
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.logo-icon svg {
  width: 32px;
  height: 32px;
}

.logo-text h1 {
  font-size: 1.875rem;
  font-weight: 800;
  color: #1a202c;
  margin: 0;
}

.logo-text p {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.refresh-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 10px rgba(59, 130, 246, 0.3);
}

.refresh-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.4);
}

.refresh-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.refresh-btn svg {
  width: 20px;
  height: 20px;
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Tabs Container */
.tabs-container {
  margin-bottom: 2rem;
}

.tabs-nav {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow-x: auto;
}

.tab-btn {
  flex: 1;
  min-width: 140px;
  background: transparent;
  border: 2px solid transparent;
  border-radius: 12px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
}

.tab-btn:hover {
  background: #f8fafc;
}

.tab-btn.active {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border-color: transparent;
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
}

.tab-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.tab-icon {
  width: 24px;
  height: 24px;
  color: #64748b;
  transition: color 0.3s ease;
}

.tab-btn.active .tab-icon {
  color: white;
}

.tab-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #64748b;
  transition: color 0.3s ease;
}

.tab-btn.active .tab-label {
  color: white;
}

.tab-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background: #ef4444;
  color: white;
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.125rem 0.5rem;
  border-radius: 10px;
  min-width: 20px;
  text-align: center;
}

/* Tab Content */
.tab-pane {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Responsive */
@media (max-width: 1024px) {
  .admin-dashboard {
    padding: 1rem;
  }

  .dashboard-header {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }

  .header-left,
  .header-right {
    width: 100%;
    justify-content: center;
  }

  .tabs-nav {
    flex-wrap: wrap;
  }

  .tab-btn {
    min-width: calc(50% - 0.5rem);
  }
}

@media (max-width: 640px) {
  .logo-text h1 {
    font-size: 1.5rem;
  }

  .tab-btn {
    min-width: 100%;
  }
}
</style>
